import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ProjectDetailDTO, ProjectFeedbackDTO } from "@app-factory/shared";
import { api } from "@/lib/api";
import { cs } from "@/i18n/cs";
import { Btn, EmptyState, Panel, SectionHeader, inputClass } from "@/design/primitives";
import { relativeTime } from "@/lib/format";
import { canContribute, useProjectRole } from "../project-data";

/** FEEDBACK: jednosměrný chat ownera — worker připomínky převádí na tasky. */
export function FeedbackTab({ project }: { project: ProjectDetailDTO }) {
  const role = useProjectRole(project);
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["section", project.id, "feedback"],
    queryFn: () => api.feedback(project.id),
  });
  const [text, setText] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const send = useMutation({
    mutationFn: () => api.createFeedback(project.id, text.trim(), file ?? undefined),
    onSuccess: () => {
      setText("");
      setFile(null);
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ["section", project.id, "feedback"] });
    },
    onError: (err) => setError(err instanceof Error ? err.message : cs.common.error),
  });
  const canSend = (text.trim().length > 0 || file !== null) && !send.isPending;

  const items = query.data?.feedback ?? [];
  return (
    <div className="mt-4 mx-auto max-w-3xl">
      <div className="mb-3 flex flex-wrap items-baseline gap-2">
        <SectionHeader label={cs.feedback.title} />
        <span className="text-[11px]" style={{ color: "var(--text-tertiary)" }}>
          {cs.feedback.subtitle}
        </span>
      </div>

      {items.length === 0 ? (
        <Panel className="p-8">
          <EmptyState title={cs.feedback.empty} note={cs.feedback.emptyNote} />
        </Panel>
      ) : (
        <div className="flex flex-col gap-2">
          {items.map((f) => (
            <FeedbackMessage key={f.id} project={project} feedback={f} />
          ))}
        </div>
      )}

      {canContribute(role) ? (
        <Panel brackets className="mt-4 p-3" data-testid="feedback-form">
          <textarea
            className={`${inputClass} min-h-[64px] resize-y`}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey && canSend) {
                e.preventDefault();
                send.mutate();
              }
            }}
            placeholder={cs.feedback.placeholder}
            data-testid="feedback-text"
          />
          {error ? (
            <div className="mt-2 text-[11px]" style={{ color: "var(--critical)" }}>
              ⚠ {error}
            </div>
          ) : null}
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
            <span className="flex min-w-0 flex-wrap items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                data-testid="feedback-file"
              />
              <Btn onClick={() => fileInputRef.current?.click()}>{cs.feedback.attach}</Btn>
              {file ? (
                <span className="min-w-0 break-all text-[11px]" style={{ color: "var(--text-secondary)" }}>
                  {file.name}{" "}
                  <button
                    type="button"
                    className="cursor-pointer"
                    style={{ color: "var(--critical)" }}
                    onClick={() => setFile(null)}
                  >
                    ✕
                  </button>
                </span>
              ) : null}
            </span>
            <Btn variant="primary" disabled={!canSend} onClick={() => send.mutate()} data-testid="feedback-send">
              {cs.feedback.send}
            </Btn>
          </div>
        </Panel>
      ) : null}
    </div>
  );
}

function FeedbackMessage({
  project,
  feedback,
}: {
  project: ProjectDetailDTO;
  feedback: ProjectFeedbackDTO;
}) {
  const attachmentUrl = feedback.hasAttachment
    ? api.feedbackAttachmentUrl(project.id, feedback.id)
    : null;
  return (
    <Panel className="p-3" data-testid="feedback-message">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[11px] tracking-[0.1em]" style={{ color: "var(--accent)" }}>
          {feedback.authorLabel.toUpperCase()}
        </span>
        <span className="text-[11px]" style={{ color: "var(--text-tertiary)" }}>
          {relativeTime(feedback.createdAt)}
        </span>
      </div>
      {feedback.text ? (
        <div
          className="mt-1.5 whitespace-pre-wrap break-words text-[12px] leading-relaxed"
          style={{ color: "var(--text-primary)" }}
        >
          {feedback.text}
        </div>
      ) : null}
      {attachmentUrl ? (
        <a href={attachmentUrl} target="_blank" rel="noreferrer" className="mt-2 inline-block">
          <img
            src={attachmentUrl}
            alt={feedback.attachmentFilename ?? "příloha"}
            loading="lazy"
            className="max-h-[220px] max-w-full"
            style={{ borderRadius: "var(--radius-box)", border: "1px solid var(--border-subtle)" }}
          />
        </a>
      ) : null}
      <div className="mt-2 break-words text-[11px]" style={{ color: "var(--text-tertiary)" }}>
        {feedback.processedAt
          ? feedback.taskRefs.length
            ? `✓ ${cs.feedback.processedInto} ${feedback.taskRefs.map((t) => `#${t.number} ${t.title}`).join(" · ")}`
            : `✓ ${cs.feedback.processedEmpty}${feedback.processedNote ? ` — ${feedback.processedNote}` : ""}`
          : `⏳ ${cs.feedback.waiting}`}
      </div>
    </Panel>
  );
}
